import { useMemo, useState } from "react";
import { Select } from "../Select/select";
import { Input } from "../TextInput";
import classNames from "classnames";

export type DateValue = {
  day: number;
  month: number;
  year: number;
};

type PartialDateValue = Partial<DateValue>;

type DatePickerProps = {
  label?: string;
  error?: string;
  translations: {
    months: {
      january: string;
      february: string;
      march: string;
      april: string;
      may: string;
      june: string;
      july: string;
      august: string;
      september: string;
      october: string;
      november: string;
      december: string;
    };
    placeholders: {
      day: string;
      month: string;
      year: string;
    };
  };
} & (
  | {
      value: DateValue | undefined;
      onChange: (value: DateValue) => void;
    }
  | {
      value: undefined;
      onChange: undefined;
    }
);

const convertValue = (value: unknown): number | undefined => {
  if (typeof value === "number") {
    return value;
  }

  if (value === "") {
    return undefined;
  }

  if (typeof value === "string") {
    if (/^\d+$/.test(value)) {
      return parseInt(value);
    }
    return;
  }

  throw new Error("Invalid value type. This should never happen");
};

const isCompleteDateValue = (value: PartialDateValue): value is DateValue => {
  return (
    value.day !== undefined &&
    value.month !== undefined &&
    value.year !== undefined
  );
};

// this component only updates the onChange method if the date is complete
export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  error,
  translations,
}) => {
  const [innerValue, setInnerValue] = useState<PartialDateValue>(value ?? {});

  const monthOptions = useMemo(
    () => [
      { value: "1", label: translations.months.january },
      { value: "2", label: translations.months.february },
      { value: "3", label: translations.months.march },
      { value: "4", label: translations.months.april },
      { value: "5", label: translations.months.may },
      { value: "6", label: translations.months.june },
      { value: "7", label: translations.months.july },
      { value: "8", label: translations.months.august },
      { value: "9", label: translations.months.september },
      { value: "10", label: translations.months.october },
      { value: "11", label: translations.months.november },
      { value: "12", label: translations.months.december },
    ],
    [translations.months]
  );

  const innerOnChange = (key: keyof PartialDateValue, value: unknown) => {
    const convertedValue = convertValue(value);

    if (
      key === "day" &&
      !!convertedValue &&
      (convertedValue < 1 || convertedValue > 31)
    ) {
      return;
    }

    if (
      key === "year" &&
      !!convertedValue &&
      (convertedValue < 1 || convertedValue > 2100)
    ) {
      return;
    }

    const newDateValue = {
      ...innerValue,
      [key]: convertedValue,
    };

    setInnerValue(newDateValue);

    if (onChange && isCompleteDateValue(newDateValue)) {
      onChange(newDateValue);
    }
  };

  return (
    <div>
      {label && (
        <span
          className={classNames("text-sm mb-1 font-medium ml-0.5", {
            "text-gray-700": !error,
          })}
        >
          {label}
        </span>
      )}
      <div className="grid grid-cols-3 gap-2 ">
        <Input
          value={innerValue.day ?? ""}
          onChange={(e) => innerOnChange("day", e.target.value)}
          className="w-full"
          placeholder={translations.placeholders.day}
          type="number"
          inputMode="numeric"
          error={!!error}
        />
        <Select
          placeholder={translations.placeholders.month}
          options={monthOptions}
          className="w-full"
          value={innerValue.month?.toString() ?? ""}
          onChange={(selected) => {
            innerOnChange("month", selected);
          }}
          error={!!error}
        />
        <Input
          value={innerValue.year ?? ""}
          className="w-full"
          placeholder={translations.placeholders.year}
          type="number"
          inputMode="numeric"
          onChange={(e) => innerOnChange("year", e.target.value)}
          error={!!error}
        />
      </div>
      {typeof error === "string" && (
        <p className="ml-1 mt-1 text-sm text-error">{error}</p>
      )}
    </div>
  );
};
